/**
 * 场景工具箱：把「画地图」变成几行代码。
 *
 * 场景文件只描述自己的地图与器物，「画地图 -> 地图文档 -> 可进的场景」这段冗活在这里。
 * 带来的好处：新场景 = 一小段 draw() + 一张器物表，不必再重复写 Scene / Tilemap / 系统挂载。
 */
import { MAP_CHARS } from '../render/tiles_art.js';

/** 确定性随机：同一张地图每次进都一样，方便排错与测试 */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 字符画布。 fill 是底色字符（MAP_CHARS 里的键）。
 * 常用地形字符：'#'墙 '.'木地板 ','草 ':'土 '~'石 '+'门槛 'w'水 'e'水岸 's'沙
 *              'd'泥地 'f'农田 'r'土路 '/'车道线 'n'木桥 '^'关卡石 '='街砖 'o'广场石 'b'屋基
 */
export function brush(w, h, fill = ',') {
  const grid = [];
  for (let y = 0; y < h; y++) {
    const row = new Array(w);
    for (let x = 0; x < w; x++) row[x] = fill;
    grid.push(row);
  }
  const inside = (x, y) => x >= 0 && y >= 0 && x < w && y < h;

  const api = {
    w, h, grid,
    set(x, y, ch) {
      if (inside(x, y)) grid[y][x] = ch;
      return api;
    },
    get(x, y) { return inside(x, y) ? grid[y][x] : '#'; },
    /** 实心矩形 */
    rect(x, y, rw, rh, ch) {
      for (let j = 0; j < rh; j++) for (let i = 0; i < rw; i++) api.set(x + i, y + j, ch);
      return api;
    },
    /** 空心矩形（描边） */
    frame(x, y, rw, rh, ch) {
      for (let i = 0; i < rw; i++) { api.set(x + i, y, ch); api.set(x + i, y + rh - 1, ch); }
      for (let j = 0; j < rh; j++) { api.set(x, y + j, ch); api.set(x + rw - 1, y + j, ch); }
      return api;
    },
    border(ch = '#') { return api.frame(0, 0, w, h, ch); },
    hline(x1, x2, y, ch) {
      const [a, b] = x1 <= x2 ? [x1, x2] : [x2, x1];
      for (let x = a; x <= b; x++) api.set(x, y, ch);
      return api;
    },
    vline(y1, y2, x, ch) {
      const [a, b] = y1 <= y2 ? [y1, y2] : [y2, y1];
      for (let y = a; y <= b; y++) api.set(x, y, ch);
      return api;
    },
    /** 在矩形内按条件替换 */
    fillIf(x, y, rw, rh, ch, fn) {
      for (let j = 0; j < rh; j++) {
        for (let i = 0; i < rw; i++) {
          const px = x + i, py = y + j;
          if (inside(px, py) && (!fn || fn(grid[py][px], px, py))) api.set(px, py, ch);
        }
      }
      return api;
    },
    /** 随机撒点：rng 保证每次一样 */
    scatter(ch, count, { rng = Math.random, area = null, only = null } = {}) {
      const [ax, ay, aw, ah] = area || [1, 1, w - 2, h - 2];
      let guard = count * 40;
      let done = 0;
      while (done < count && guard-- > 0) {
        const x = ax + Math.floor(rng() * aw);
        const y = ay + Math.floor(rng() * ah);
        if (!inside(x, y)) continue;
        if (only && !only(grid[y][x], x, y)) continue;
        grid[y][x] = ch;
        done++;
      }
      return api;
    },
    rows() { return grid.map((r) => r.join('')); },
  };
  return api;
}

/** 由门户表生成器物条目；落在 documents 的 props 里 */
function portalProps(list = []) {
  return list.map((p) => ({
    type: 'portal',
    gx: p.gx,
    gy: p.gy,
    state: {
      to: p.to,
      spawn: p.spawn || null,
      label: p.label || '',
      need: p.need || null,
      cost: p.cost || null,
      hours: p.hours || null,
    },
  }));
}

/**
 * 场景定义 -> 地图文档（喂给 createDocScene 的那一版 JSON）。
 *
 * def = {
 *   id, name, region, tier, need, cost, hours,
 *   w, h, fill, tileSize, spawn:{gx,gy},
 *   draw(b, rng)        画地图
 *   props:[{type,gx,gy,state}]   场景自带器物
 *   portals:[{to,gx,gy,spawn,label,need,cost}]
 * }
 */
export function makeSceneDoc(def) {
  const b = brush(def.w, def.h, def.fill || ',');
  const rng = mulberry32(hashSeed(def.id));
  if (typeof def.draw === 'function') def.draw(b, rng);
  const spawn = def.spawn || { gx: Math.floor(def.w / 2), gy: Math.floor(def.h / 2) };
  return {
    id: def.id,
    name: def.name || def.id,
    tileSize: def.tileSize || 40,
    rows: b.rows(),
    spawn,
    props: [...(def.props || []), ...portalProps(def.portals || [])],
    // 下面这几项是给 world.js 记账用的，createDocScene 不读
    meta: {
      region: def.region || 'suburb',
      tier: def.tier == null ? 0 : def.tier,
      need: def.need || null,
      cost: def.cost || null,
      hours: def.hours || null,
      indoor: !!def.indoor,
      kind: def.kind || null,
      node: def.node || null,
      desc: def.desc || '',
      shopId: def.shopId || null,
    },
  };
}

export function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** 一排柜台：每格一个 counter，state.shop 指名这是谁家的买卖 */
export function counterRow(x, y, len, kind) {
  const out = [];
  for (let i = 0; i < len; i++) {
    out.push({ type: 'counter', gx: x + i, gy: y, state: { shop: kind, kind } });
  }
  return out;
}

/** 把每个kind dotmaps 转换成不可行走的墙：给长度这片区域描木地板 */
export function indoorFloor(b, x, y, w, h, floor = '.') {
  b.rect(x, y, w, h, floor);
  b.frame(x - 1, y - 1, w + 2, h + 2, '#');
  return b;
}

export { MAP_CHARS };
